import { useState, useEffect, useRef, useCallback } from "react";
import { setAppBit } from "../api/sacada";
import useWebSocket from "../hooks/useWebSockets";
import { useAuth } from "../context/AuthContext";

/* ─── PALETTE ─── */
const C = {
  teal:      "#057c82",
  tealDark:  "#045a5f",
  tealLight: "#e8f4f5",
  tealBg:    "#f0f8f8",
  border:    "#b8d8da",
  border2:   "#d0e8ea",
  white:     "#ffffff",
  gray:      "#555454",
  grayLight: "#888",
  green:     "#27ae60",
  orange:    "#e67e22",
  red:       "#e74c3c",
  purple:    "#8e44ad",
};

const LEVEL_LOW = 15;
const LEVEL_MED = 50;
const LEVEL_HIGH = 90;
const PLC_STAGE_DURATION_MS = 5000;
const PLC_STAGE_LEVELS = { low: LEVEL_LOW, medium: LEVEL_MED, high: LEVEL_HIGH };

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&display=swap');

.scada-root *, .scada-root *::before, .scada-root *::after {
  box-sizing: border-box;
  font-family: 'Rajdhani', 'Segoe UI', sans-serif;
}

.glass-card {
  background: rgba(255,255,255,0.78);
  border: 1px solid rgba(184,216,218,0.7);
  border-radius: 12px;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 4px 18px rgba(5,124,130,0.07);
}

@keyframes glow-green  { 0%,100%{box-shadow:0 0 6px 2px #27ae6088} 50%{box-shadow:0 0 14px 5px #27ae6066} }
@keyframes glow-orange { 0%,100%{box-shadow:0 0 6px 2px #e67e2288} 50%{box-shadow:0 0 14px 5px #e67e2266} }
@keyframes glow-red    { 0%,100%{box-shadow:0 0 6px 2px #e74c3c88} 50%{box-shadow:0 0 14px 5px #e74c3c66} }
@keyframes glow-teal   { 0%,100%{box-shadow:0 0 6px 2px #057c8288} 50%{box-shadow:0 0 14px 5px #057c8266} }
@keyframes pulse-ring  { 0%{transform:scale(1);opacity:.8} 100%{transform:scale(1.6);opacity:0} }
@keyframes notif-in    { from{opacity:0;transform:translateY(-16px)} to{opacity:1;transform:translateY(0)} }
@keyframes wave-move   { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }

.btn-marche {
  background: ${C.teal};
  color: #fff;
  border: 2px solid ${C.tealDark};
  border-radius: 9px;
  padding: 13px 0;
  width: 100%;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 2px;
  cursor: pointer;
  transition: background .18s, transform .1s, box-shadow .18s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.btn-marche:hover  { background: ${C.tealDark}; box-shadow: 0 4px 18px ${C.teal}55; }
.btn-marche:active { transform: scale(.97); }
.btn-marche.active { box-shadow: 0 0 0 3px ${C.teal}66, 0 4px 18px ${C.teal}44; }

.btn-arret {
  background: #fff;
  color: ${C.red};
  border: 2px solid ${C.red};
  border-radius: 9px;
  padding: 13px 0;
  width: 100%;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 2px;
  cursor: pointer;
  transition: background .18s, transform .1s, box-shadow .18s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.btn-arret:hover  { background: #fff5f5; box-shadow: 0 4px 18px ${C.red}33; }
.btn-arret:active { transform: scale(.97); }

.btn-sensor {
  width: 100%;
  display: flex; align-items: center; gap: 9px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all .18s;
  text-align: left;
}
.btn-sensor:active { transform: scale(.98); }
.btn-sensor.off { background: ${C.tealBg}; border: 1.5px solid ${C.border2}; }
.btn-sensor.on-red    { background: #fdecea; border: 1.5px solid ${C.red}; }
.btn-sensor.on-green  { background: #eafaf0; border: 1.5px solid ${C.green}; }

/* ── Boutons industriels ON/OFF pour Remplissage / Vidange ── */
.btn-industriel {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 12px 8px;
  border-radius: 9px;
  cursor: pointer;
  transition: background .18s, color .18s, box-shadow .18s, transform .1s;
  font-weight: 700;
  letter-spacing: 1.5px;
}
.btn-industriel:active { transform: scale(.97); }

.btn-industriel.off-green {
  background: #fff;
  color: ${C.green};
  border: 2px solid ${C.green};
}
.btn-industriel.off-green:hover { background: #f3fbf6; }

.btn-industriel.on-green {
  background: ${C.green};
  color: #fff;
  border: 2px solid #1f8a4c;
  box-shadow: 0 0 0 3px ${C.green}33, 0 4px 14px ${C.green}44;
}

.btn-industriel.off-red {
  background: #fff;
  color: ${C.red};
  border: 2px solid ${C.red};
}
.btn-industriel.off-red:hover { background: #fff5f5; }

.btn-industriel.on-red {
  background: ${C.red};
  color: #fff;
  border: 2px solid #c0392b;
  box-shadow: 0 0 0 3px ${C.red}33, 0 4px 14px ${C.red}44;
}

.btn-industriel:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn-marche:disabled,
.btn-arret:disabled,
.btn-industriel:disabled {
  box-shadow: none;
  transform: none;
}

.reservoir-status-banner {
  max-width: 1052px;
  margin: 12px auto 0;
}

.reservoir-layout {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr) 190px;
  gap: 16px;
  padding: 16px 24px;
  max-width: 1100px;
  margin: 0 auto;
  align-items: start;
  min-width: 0;
}

.reservoir-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.reservoir-visual {
  min-width: 0;
}

@media (max-width: 900px) {
  .reservoir-status-banner {
    margin-left: 16px;
    margin-right: 16px;
  }

  .reservoir-layout {
    grid-template-columns: minmax(180px, .8fr) minmax(0, 1.2fr);
    padding: 16px;
  }

  .reservoir-side-column {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .reservoir-status-banner {
    margin: 10px 10px 0;
  }

  .reservoir-layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    padding: 12px 10px 20px;
  }

  .reservoir-visual {
    grid-row: 1;
  }

  .reservoir-command-column {
    grid-row: 2;
  }

  .reservoir-side-column {
    grid-column: auto;
    grid-row: 3;
    display: flex;
  }
}

.notif-enter { animation: notif-in .35s cubic-bezier(.22,1,.36,1) forwards; }

.s-title {
  font-size: 9px;
  font-weight: 700;
  color: ${C.teal};
  letter-spacing: 2px;
  text-transform: uppercase;
  border-bottom: 1px solid ${C.border2};
  padding-bottom: 5px;
  margin-bottom: 10px;
}
`;

function Led({ on, color = "teal", size = 13, label, sublabel }) {
  const glowMap = { green: "glow-green", orange: "glow-orange", red: "glow-red", teal: "glow-teal" };
  const bgMap   = { green: C.green, orange: C.orange, red: C.red, teal: C.teal };
  return (
    <div style={{ display:"flex", alignItems:"center", gap:9, padding:"7px 9px", background: C.tealBg, border:`1px solid ${C.border2}`, borderRadius:8 }}>
      <div style={{ position:"relative", flexShrink:0 }}>
        <div style={{
          width: size, height: size, borderRadius:"50%",
          background: on ? bgMap[color] : "#d8e8ea",
          border: `1.5px solid ${on ? bgMap[color] : "#b8cece"}`,
          animation: on ? `${glowMap[color]} 1.4s ease-in-out infinite` : "none",
          transition: "background .3s",
        }}/>
        {on && <div style={{
          position:"absolute", top:-3, left:-3,
          width: size+6, height: size+6, borderRadius:"50%",
          border: `1.5px solid ${bgMap[color]}55`,
          animation: "pulse-ring 1.4s ease-out infinite",
          pointerEvents:"none",
        }}/>}        
      </div>
      {label && (
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:"#333", letterSpacing:.4 }}>{label}</div>
          {sublabel && <div style={{ fontSize:9, color:C.grayLight, marginTop:1 }}>{sublabel}</div>}
        </div>
      )}
    </div>
  );
}

/* L'indicateur suit le pourcentage uniquement en simulation. En source PLC,
   l'aiguille accompagne l'animation estimée entre deux paliers, tandis que la
   couleur et le libellé restent ceux du dernier palier TOR confirmé. */
function LevelGauge({ level, stage, showPercent }) {
  const stageColor = { low: C.red, medium: C.orange, high: C.green }[stage] ?? C.grayLight;
  const stageLabel = {
    low: "NIVEAU BAS",
    medium: "NIVEAU MOYEN",
    high: "NIVEAU HAUT",
  }[stage] ?? "NIVEAU INCONNU";

  const cx = 100, cy = 100, r = 78;
  const polarToXY = (deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const arcPath = (startDeg, endDeg) => {
    const s = polarToXY(startDeg), e = polarToXY(endDeg);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M${s.x} ${s.y} A${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const clamped = Math.min(100, Math.max(0, level));
  const angle = -90 + (clamped / 100) * 180; // -90° (bas) → +90° (haut)
  const needle = polarToXY(angle);

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <svg width="100%" viewBox="0 0 200 118" style={{ overflow:"visible", display:"block" }}>
        <path d={arcPath(-90,-30)} stroke={C.red}    strokeWidth={14} fill="none" strokeLinecap="round" opacity={stage==="low"  ? 1 : 0.28} style={{ transition:"opacity .35s" }}/>
        <path d={arcPath(-30, 30)} stroke={C.orange} strokeWidth={14} fill="none" strokeLinecap="round" opacity={stage==="medium"  ? 1 : 0.28} style={{ transition:"opacity .35s" }}/>
        <path d={arcPath( 30, 90)} stroke={C.green}  strokeWidth={14} fill="none" strokeLinecap="round" opacity={stage==="high" ? 1 : 0.28} style={{ transition:"opacity .35s" }}/>

        <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke={stageColor} strokeWidth={3.5} strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r={7} fill={stageColor} stroke="#fff" strokeWidth={2}/>

        <text x={16}  y={112} fontSize={9} fill="#888" fontFamily="Rajdhani,sans-serif">BAS</text>
        <text x={100} y={16}  fontSize={9} fill="#888" fontFamily="Rajdhani,sans-serif" textAnchor="middle">MOY.</text>
        <text x={184} y={112} fontSize={9} fill="#888" fontFamily="Rajdhani,sans-serif" textAnchor="end">HAUT</text>
      </svg>
      <div style={{ fontSize:22, fontWeight:700, color: stageColor, fontFamily:"Rajdhani,monospace", transition:"color .3s" }}>
        {showPercent ? `${Math.round(clamped)}%` : "PALIER"}
      </div>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:1, color: stageColor, transition:"color .3s" }}>
        {stageLabel}
      </div>
    </div>
  );
}

function SensorButton({ on, color, label, onClick, disabled = false, disabledLabel = "COMMANDE DÉSACTIVÉE" }) {
  const variant = color === "red" ? "red" : "green";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      className={`btn-industriel ${on ? `on-${variant}` : `off-${variant}`}`}
    >
      <span style={{ display:"flex", alignItems:"center", gap:8, fontSize:14 }}>
        <span style={{ fontSize:11 }}>{on ? "▶" : "■"}</span>
        {label.toUpperCase()}
      </span>
      <span style={{ fontSize:9, fontWeight:600, letterSpacing:.5, opacity:.85 }}>
        {disabled
          ? disabledLabel
          : on
            ? "ACTIF — sens en cours"
            : "ARRÊTÉ — cliquer pour démarrer"}
      </span>
    </button>
  );
}

function Notif({ msg, type = "alarm", onClose }) {
  const bg = type === "info" ? C.teal : type === "success" ? C.green : C.red;
  const icon = type === "info" ? "ℹ" : type === "success" ? "✓" : "⚠";
  return (
    <div className="notif-enter" style={{
      background:"#fff", borderLeft:`5px solid ${bg}`,
      border:`1.5px solid ${bg}66`, borderRadius:9,
      padding:"8px 14px", display:"flex", alignItems:"center", gap:10,
      boxShadow:`0 4px 18px ${bg}22`, pointerEvents:"auto", width:"100%",
    }}>
      <span style={{ color:bg, fontSize:17, flexShrink:0 }}>{icon}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:12, fontWeight:700, color:bg, letterSpacing:.8 }}>{msg.title}</div>
        {msg.sub && <div style={{ fontSize:10, color:C.grayLight, marginTop:1 }}>{msg.sub}</div>}
      </div>
      <span onClick={onClose} style={{ fontSize:14, color:"#ccc", cursor:"pointer", padding:"0 4px", lineHeight:1 }}>✕</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   RÉSERVOIR SVG — INCHANGÉ : les "sorties" (Niv. Haut / Niv.
   Moy. / Niv. Bas à côté du réservoir) et la pompe restent
   pilotées exactement comme avant par les LEDs réelles du PLC
   (outputLow/outputMedium/outputHigh/outputPump).
   ───────────────────────────────────────────────────────── */
function ReservoirSVG({ level, stageLabel, showPercent, outputLow, outputMedium, outputHigh, outputPump }) {
  const svgRef = useRef(null);
  const fRef = useRef(0);
  const targetLevelRef = useRef(level);
  const renderedLevelRef = useRef(level);
  const pumpRef = useRef(outputPump);
  const tx = 118, ty = 22, tw = 110, th = 200;

  useEffect(() => {
    targetLevelRef.current = level;
  }, [level]);

  useEffect(() => {
    pumpRef.current = outputPump;
  }, [outputPump]);

  useEffect(() => {
    let af;
    let previousFrameAt = performance.now();
    const step = (frameAt) => {
      fRef.current++;
      const svg = svgRef.current;
      if (!svg) { af = requestAnimationFrame(step); return; }

      // Chaque message WebSocket reste la cible commune, mais les quelques
      // pixels entre deux messages sont interpolés à 60 fps. Cela ne crée pas
      // une seconde simulation locale : toutes les fenêtres convergent vers
      // les mêmes valeurs backend avec un retard visuel maximal d'environ 180 ms.
      const elapsedMs = Math.min(100, Math.max(0, frameAt - previousFrameAt));
      previousFrameAt = frameAt;
      const difference = targetLevelRef.current - renderedLevelRef.current;
      const interpolation = Math.min(1, elapsedMs / 180);
      renderedLevelRef.current += difference * interpolation;
      if (Math.abs(difference) < 0.02) {
        renderedLevelRef.current = targetLevelRef.current;
      }

      const wH = th * (renderedLevelRef.current / 100);
      const wY = ty + th - wH;
      const wr = svg.getElementById("water-rect");
      const ww = svg.getElementById("water-wave-g");
      const wt = svg.getElementById("water-top");
      if (wr) { wr.setAttribute("height", Math.max(0, wH)); wr.setAttribute("y", wY); }
      if (ww) { ww.setAttribute("transform", `translate(0,${wY})`); }
      if (wt) { wt.setAttribute("cy", wY); }

      const rotor = svg.getElementById("pump-rotor");
      if (rotor && pumpRef.current) rotor.setAttribute("transform", `rotate(${fRef.current * 4},35,185)`);

      const flow = svg.getElementById("flow-pipe-anim");
      if (flow) {
        flow.setAttribute("stroke-dashoffset", pumpRef.current ? String(-(fRef.current % 20) * 1.2) : "0");
        flow.setAttribute("stroke", pumpRef.current ? C.teal : "transparent");
      }

      af = requestAnimationFrame(step);
    };
    af = requestAnimationFrame(step);
    return () => cancelAnimationFrame(af);
  }, []);


  return (
    <svg
      ref={svgRef}
      data-testid="reservoir-tank"
      data-visual-level={level.toFixed(1)}
      width="100%"
      viewBox="0 0 340 255"
      style={{ display:"block", overflow:"visible" }}
    >
      <defs>
        <clipPath id="tank-clip"><rect x={tx} y={ty} width={tw} height={th} rx="4"/></clipPath>
        <linearGradient id="water-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.teal} stopOpacity="0.85"/>
          <stop offset="100%" stopColor={C.tealDark} stopOpacity="0.95"/>
        </linearGradient>
      </defs>

      <rect x={tx+tw/2-5} y={0} width={10} height={ty+2} rx="3" fill="#c8dfe0" stroke={C.border} strokeWidth="1"/>
      <rect id="flow-pipe-anim" x={tx+tw/2-4} y={0} width={8} height={ty} fill="none" stroke="transparent" strokeWidth={6} strokeDasharray="6 5" rx="3"/>

      <path d={`M61 185 H${tx}`} fill="none" stroke="#c8dfe0" strokeWidth={10} strokeLinecap="round"/>

      <rect x={tx} y={ty} width={tw} height={th} rx="5" fill={C.tealLight} stroke={C.teal} strokeWidth="2.5"/>

      <g clipPath="url(#tank-clip)">
        <rect id="water-rect" x={tx} y={ty+th} width={tw} height={0} fill="url(#water-grad)" opacity="0.9"/>
        <g id="water-wave-g" transform={`translate(0,${ty+th})`}>
          <g style={{ animation: outputPump ? "wave-move 1.8s linear infinite" : "none" }}>
            <path d={`M${tx},0 Q${tx+14},-4 ${tx+28},0 Q${tx+42},4 ${tx+56},0 Q${tx+70},-4 ${tx+84},0 Q${tx+98},4 ${tx+112},0 Q${tx+126},-4 ${tx+140},0 Q${tx+154},4 ${tx+168},0 Q${tx+182},-4 ${tx+196},0 Q${tx+210},4 ${tx+224},0 L${tx+224},8 L${tx},8 Z`} fill="rgba(255,255,255,0.22)"/>
          </g>
        </g>
        <ellipse id="water-top" cx={tx+tw/2} cy={ty+th} rx={tw/2-1} ry={7} fill={C.teal} opacity="0.6"/>
      </g>

      <rect x={tx} y={ty} width={tw} height={th} rx="5" fill="none" stroke={C.teal} strokeWidth="2.5"/>

      {showPercent && [0,25,50,75,100].map(pct => {
        const gy = ty + th - (pct/100)*th;
        return (
          <g key={pct}>
            <line x1={tx-5} y1={gy} x2={tx} y2={gy} stroke={C.teal} strokeWidth="1.5"/>
            <text x={tx-8} y={gy+3} textAnchor="end" fontSize={9} fill="#888" fontFamily="Rajdhani,sans-serif">{pct}%</text>
          </g>
        );
      })}

      <line x1={tx} y1={ty+th*0.25} x2={tx+tw} y2={ty+th*0.25} stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="4 4"/>
      <line x1={tx} y1={ty+th*0.5}  x2={tx+tw} y2={ty+th*0.5}  stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="4 4"/>
      <line x1={tx} y1={ty+th*0.75} x2={tx+tw} y2={ty+th*0.75} stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="4 4"/>

      {/* Niv. Haut */}
      <line x1={tx+tw} y1={ty+th*0.22} x2={tx+tw+30} y2={ty+th*0.22} stroke={C.border} strokeWidth="1" strokeDasharray="3 3"/>
      <circle cx={tx+tw+38} cy={ty+th*0.22} r={7} fill={outputHigh ? C.green : C.tealLight} stroke={outputHigh ? C.green : C.border} strokeWidth="1.5" style={{ filter: outputHigh ? "drop-shadow(0 0 5px #27ae60)" : "none" }}/>
      <text x={tx+tw+48} y={ty+th*0.22+4} fontSize={9} fill="#555" fontFamily="Rajdhani,sans-serif" fontWeight={600}>Niv. Haut</text>

      {/* Niv. Moy. */}
      <line x1={tx+tw} y1={ty+th*0.5} x2={tx+tw+30} y2={ty+th*0.5} stroke={C.border} strokeWidth="1" strokeDasharray="3 3"/>
      <circle cx={tx+tw+38} cy={ty+th*0.5} r={7} fill={outputMedium ? C.orange : C.tealLight} stroke={outputMedium ? C.orange : C.border} strokeWidth="1.5" style={{ filter: outputMedium ? "drop-shadow(0 0 5px #e67e22)" : "none" }}/>
      <text x={tx+tw+48} y={ty+th*0.5+4} fontSize={9} fill="#555" fontFamily="Rajdhani,sans-serif" fontWeight={600}>Niv. Moy.</text>

      {/* Niv. Bas */}
      <line x1={tx+tw} y1={ty+th*0.78} x2={tx+tw+30} y2={ty+th*0.78} stroke={C.border} strokeWidth="1" strokeDasharray="3 3"/>
      <circle cx={tx+tw+38} cy={ty+th*0.78} r={7} fill={outputLow ? C.red : C.tealLight} stroke={outputLow ? C.red : C.border} strokeWidth="1.5" style={{ filter: outputLow ? "drop-shadow(0 0 5px #e74c3c)" : "none" }}/>
      <text x={tx+tw+48} y={ty+th*0.78+4} fontSize={9} fill="#555" fontFamily="Rajdhani,sans-serif" fontWeight={600}>Niv. Bas</text>

      <text x={tx+tw/2} y={ty+th/2+6} textAnchor="middle" fontSize={20} fontWeight={700} fill="rgba(255,255,255,0.92)" fontFamily="Rajdhani,monospace" style={{ textShadow:"0 1px 4px rgba(0,0,0,0.3)" }}>
        {showPercent ? `${Math.round(level)}%` : stageLabel}
      </text>

      <circle cx={35} cy={185} r={26} fill={C.tealLight} stroke={C.teal} strokeWidth="2"/>
      <circle cx={35} cy={185} r={18} fill={outputPump ? "rgba(5,124,130,0.12)" : "#f8fcfc"} stroke={C.border} strokeWidth="1"/>
      <g id="pump-rotor">
        {[0,120,240].map(deg => {
          const rad = deg * Math.PI / 180;
          return <line key={deg} x1={35} y1={185} x2={35+Math.cos(rad)*14} y2={185+Math.sin(rad)*14} stroke={outputPump ? C.teal : "#b8d8da"} strokeWidth={2.8} strokeLinecap="round"/>;
        })}
        <circle cx={35} cy={185} r={4} fill={outputPump ? C.teal : "#b8d8da"}/>
      </g>
      <rect x={8} y={172} width={18} height={26} rx="3" fill={C.tealLight} stroke={C.border} strokeWidth="1.5"/>
      <text x={35} y={220} textAnchor="middle" fontSize={9} fontWeight={700} fill="#555" fontFamily="Rajdhani,sans-serif">POMPE P-01</text>
      <text x={35} y={231} textAnchor="middle" fontSize={8} fill={outputPump ? C.teal : "#aaa"} fontFamily="Rajdhani,sans-serif">
        {outputPump ? "EN MARCHE" : "ARRÊTÉE"}
      </text>

      <path d={`M${tx+tw/2} ${ty+th} V240`} fill="none" stroke="#c8dfe0" strokeWidth={10} strokeLinecap="round"/>
      <text x={tx+tw/2} y={252} textAnchor="middle" fontSize={9} fill={C.grayLight} fontFamily="Rajdhani,sans-serif" fontWeight={700}>SORTIE</text>
    </svg>
  );
}

export default function Reservoir({ inactivePreview = false }) {
  const { token } = useAuth();
  const {
    data,
    lastMessage,
    connected,
    stale,
    error: liveDataError,
  } = useWebSocket("reservoir", !inactivePreview);
  const pendingCommandTraces = useRef(new Map());
  const [, setLastCommandLatencyMs] = useState(null);

  // ── États réels venant du PLC (utilisés pour les SORTIES et l'ÉTAT SYSTÈME — inchangé) ──
  const remplissage = data?.Remplissage ?? false;
  const vidange      = data?.Vidange ?? false;
  const ledRed    = data?.led_red ?? false;
  const ledOrange = data?.led_orange ?? false;
  const ledGreen  = data?.led_green ?? false;
  const pump      = data?.pump ?? false;
  const plcStage  = data?.level_stage ?? null;
  const sharedLevelAvailable = Number.isFinite(data?.level_percent);
  const sharedAnimationDirection = ["filling", "draining"].includes(data?.animation_direction)
    ? data.animation_direction
    : null;
  const isSimulation = lastMessage?.data_source === "simulation";
  const plcStageKnown = ["low", "medium", "high"].includes(plcStage);
  const commandsEnabled = lastMessage?.type === "live_data"
    && lastMessage?.commands_enabled === true;
  const controlsDisabled = !connected || stale || data === null || !commandsEnabled;
  const cycleControlsDisabled = controlsDisabled || (!isSimulation && !plcStageKnown);

  const [isStopping, setIsStopping] = useState(false);
  const [isSendingRemplissage, setIsSendingRemplissage] = useState(false);
  const [isSendingVidange, setIsSendingVidange] = useState(false);

  const [level, setLevel] = useState(LEVEL_LOW);
  const [plcVisualLevel, setPlcVisualLevel] = useState(LEVEL_LOW);
  const [plcMotion, setPlcMotion] = useState(null);
  const [plcCommandDirection, setPlcCommandDirection] = useState(null);
  const [ignoredPlcDirection, setIgnoredPlcDirection] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [secs, setSecs] = useState(0);
  const timerRef = useRef(null);
  const notifId = useRef(0);
  const notifTimeoutRef = useRef(null);
  const plcMotionTimerRef = useRef(null);
  const plcVisualLevelRef = useRef(LEVEL_LOW);
  const plcVisualInitializedRef = useRef(false);
  const plcDirectionRef = useRef(null);
  const ignoredPlcDirectionRef = useRef(null);
  const plcReturnTargetRef = useRef(null);
  const plcCommandDirectionRef = useRef(null);
  const plcCommandDirectionTimeoutRef = useRef(null);
  const plcMotionKeyRef = useRef(null);

  const updatePlcCommandDirection = useCallback((direction) => {
    plcCommandDirectionRef.current = direction;
    setPlcCommandDirection(direction);
  }, []);

  const updateIgnoredPlcDirection = useCallback((direction) => {
    ignoredPlcDirectionRef.current = direction;
    setIgnoredPlcDirection(direction);
  }, []);

  const setOptimisticPlcDirection = useCallback((direction) => {
    if (plcCommandDirectionTimeoutRef.current) {
      clearTimeout(plcCommandDirectionTimeoutRef.current);
    }
    updatePlcCommandDirection(direction);
    plcCommandDirectionTimeoutRef.current = setTimeout(() => {
      if (plcCommandDirectionRef.current === direction) {
        updatePlcCommandDirection(null);
      }
      plcCommandDirectionTimeoutRef.current = null;
    }, 1500);
  }, [updatePlcCommandDirection]);

  const fillingCycleActive = sharedLevelAvailable
    ? sharedAnimationDirection === "filling" || remplissage
    : plcCommandDirection === "filling"
      || (remplissage && ignoredPlcDirection !== "filling")
      || plcMotion === "filling";
  const drainingCycleActive = sharedLevelAvailable
    ? sharedAnimationDirection === "draining" || vidange
    : plcCommandDirection === "draining"
      || (vidange && ignoredPlcDirection !== "draining")
      || plcMotion === "draining";

  const stopPlcVisualMotion = useCallback(() => {
    if (plcMotionTimerRef.current) {
      clearInterval(plcMotionTimerRef.current);
      plcMotionTimerRef.current = null;
    }
  }, []);

  const setPlcVisualLevelValue = useCallback((nextLevel) => {
    plcVisualLevelRef.current = nextLevel;
    setPlcVisualLevel(nextLevel);
  }, []);

  const startPlcVisualMotion = useCallback((from, to, direction, motionKey, durationMs = PLC_STAGE_DURATION_MS) => {
    stopPlcVisualMotion();
    plcMotionKeyRef.current = motionKey;
    setPlcMotion(direction);
    setPlcVisualLevelValue(from);
    const startedAt = Date.now();
    const safeDurationMs = Math.max(200, durationMs);

    plcMotionTimerRef.current = setInterval(() => {
      const progress = Math.min(1, (Date.now() - startedAt) / safeDurationMs);
      setPlcVisualLevelValue(from + (to - from) * progress);
      if (progress >= 1) stopPlcVisualMotion();
    }, 50);
  }, [setPlcVisualLevelValue, stopPlcVisualMotion]);

  const sendTracedCommand = useCallback(async (bit, state) => {
    const correlationId = window.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    pendingCommandTraces.current.set(correlationId, performance.now());
    try {
      return await setAppBit("reservoir", bit, state, token, correlationId);
    } catch (error) {
      pendingCommandTraces.current.delete(correlationId);
      throw error;
    }
  }, [token]);

  useEffect(() => {
    const traces = lastMessage?.command_traces || [];
    traces.forEach((trace) => {
      const clickStartedAt = pendingCommandTraces.current.get(trace.correlation_id);
      if (clickStartedAt === undefined) return;
      pendingCommandTraces.current.delete(trace.correlation_id);
      requestAnimationFrame(() => {
        setLastCommandLatencyMs(Number((performance.now() - clickStartedAt).toFixed(2)));
      });
    });
  }, [lastMessage]);

  const pushNotif = useCallback((title, sub, type = "alarm") => {
    const id = ++notifId.current;
    setNotifs([{ id, title, sub, type }]);
    if (notifTimeoutRef.current) clearTimeout(notifTimeoutRef.current);
    notifTimeoutRef.current = setTimeout(() => {
      setNotifs(current => current.filter(notification => notification.id !== id));
      notifTimeoutRef.current = null;
    }, 6000);
  }, []);

  const closeNotif = useCallback(id => {
    setNotifs(n => n.filter(x => x.id !== id));
    if (notifTimeoutRef.current) {
      clearTimeout(notifTimeoutRef.current);
      notifTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    if (notifTimeoutRef.current) clearTimeout(notifTimeoutRef.current);
  }, []);

  // Chrono de marche — tourne tant que la pompe est réellement active
  useEffect(() => {
    if (pump) {
      if (!timerRef.current) {
        timerRef.current = setInterval(() => setSecs(s => s + 1), 1000);
      }
    } else {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => { clearInterval(timerRef.current); timerRef.current = null; };
  }, [pump]);

  // Commandes → PLC réel (Start / Stop, inchangées)
  const cmdMarche = useCallback(async () => {
    if (controlsDisabled || isStopping) return;
    try {
      await sendTracedCommand(0, true);
      setTimeout(
        () => sendTracedCommand(0, false).catch((err) => console.error("Erreur PLC :", err)),
        500
      );
    } catch (err) { console.error("Erreur PLC :", err); }
  }, [controlsDisabled, isStopping, sendTracedCommand]);

  const cmdArret = useCallback(async () => {
    if (isStopping || controlsDisabled) return;

    setIsStopping(true);
    try {
      await sendTracedCommand(2, false);
      await sendTracedCommand(3, false);
      await sendTracedCommand(0, false);
      await sendTracedCommand(1, true);
      await new Promise(resolve => setTimeout(resolve, 500));
      await sendTracedCommand(1, false);
      updatePlcCommandDirection(null);
      setSecs(0);
    } catch (err) {
      console.error("Erreur PLC :", err);
      try {
        await sendTracedCommand(1, false);
      } catch (releaseErr) {
        console.error("Erreur remise à zéro Arrêt :", releaseErr);
      }
    } finally {
      setIsStopping(false);
    }
  }, [isStopping, controlsDisabled, sendTracedCommand, updatePlcCommandDirection]);

  // Le programme Crouzet mémorise maintenant lui-même L31/L32. La SCADA envoie
  // donc uniquement une impulsion, comme les boutons physiques. Le retour PLC
  // Remplissage/Vidange reste la vérité commune à tous les navigateurs.
  const toggleRemplissage = useCallback(async () => {
    if (isSendingRemplissage || isSendingVidange || cycleControlsDisabled) return;
    if (plcCommandDirectionRef.current === "filling") return;
    setIsSendingRemplissage(true);

    try {
      await sendTracedCommand(2, true);
      // Compatibilité avec un ancien backend. Avec le niveau partagé actuel,
      // toutes les fenêtres attendent le même retour WebSocket.
      if (!sharedLevelAvailable) setOptimisticPlcDirection("filling");
      setTimeout(
        () => sendTracedCommand(2, false).catch((err) => console.error("Erreur PLC :", err)),
        500
      );
    } catch (err) {
      console.error("Erreur PLC :", err);
    } finally {
      setIsSendingRemplissage(false);
    }
  }, [isSendingRemplissage, isSendingVidange, cycleControlsDisabled, sendTracedCommand, setOptimisticPlcDirection, sharedLevelAvailable]);

  const toggleVidange = useCallback(async () => {
    if (isSendingVidange || isSendingRemplissage || cycleControlsDisabled) return;
    if (plcCommandDirectionRef.current === "draining") return;
    setIsSendingVidange(true);

    try {
      await sendTracedCommand(3, true);
      if (!sharedLevelAvailable) setOptimisticPlcDirection("draining");
      setTimeout(
        () => sendTracedCommand(3, false).catch((err) => console.error("Erreur PLC :", err)),
        500
      );
    } catch (err) {
      console.error("Erreur PLC :", err);
    } finally {
      setIsSendingVidange(false);
    }
  }, [isSendingVidange, isSendingRemplissage, cycleControlsDisabled, sendTracedCommand, setOptimisticPlcDirection, sharedLevelAvailable]);

  // Le niveau simulé est calculé une seule fois dans le backend partagé. Tous
  // les navigateurs reçoivent donc exactement la même valeur par WebSocket.
  useEffect(() => {
    if (!sharedLevelAvailable) return;
    setLevel(data.level_percent);
  }, [data?.level_percent, sharedLevelAvailable]);

  const stage = isSimulation
    ? level >= LEVEL_HIGH - 0.5
      ? "high"
      : level >= LEVEL_MED - 0.5
        ? "medium"
        : "low"
    : plcStage;

  // Le PLC ne fournit que trois capteurs TOR. Entre deux confirmations, la
  // hauteur d'eau est donc une animation visuelle estimée (5 s par trajet),
  // orientée par Remplissage/Vidange puis recalée sur chaque capteur réel.
  useEffect(() => {
    if (isSimulation) {
      stopPlcVisualMotion();
      updatePlcCommandDirection(null);
      updateIgnoredPlcDirection(null);
      plcDirectionRef.current = null;
      plcReturnTargetRef.current = null;
      plcVisualInitializedRef.current = false;
      plcMotionKeyRef.current = null;
      setPlcMotion(null);
      return;
    }

    // Sur le PLC réel, le backend possède désormais l'unique horloge
    // d'animation. Aucun navigateur ne calcule sa propre progression : tous
    // affichent le même niveau et le même sens reçus dans le même WebSocket.
    if (sharedLevelAvailable) {
      stopPlcVisualMotion();
      updatePlcCommandDirection(null);
      updateIgnoredPlcDirection(null);
      plcDirectionRef.current = sharedAnimationDirection;
      plcReturnTargetRef.current = null;
      plcVisualInitializedRef.current = true;
      plcMotionKeyRef.current = null;
      setPlcMotion(sharedAnimationDirection);
      setPlcVisualLevelValue(data.level_percent);
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(PLC_STAGE_LEVELS, plcStage)) {
      stopPlcVisualMotion();
      updatePlcCommandDirection(null);
      updateIgnoredPlcDirection(null);
      plcDirectionRef.current = null;
      plcReturnTargetRef.current = null;
      plcVisualInitializedRef.current = false;
      plcMotionKeyRef.current = null;
      setPlcMotion(null);
      setPlcVisualLevelValue(0);
      return;
    }

    if (!connected || stale) {
      stopPlcVisualMotion();
      updatePlcCommandDirection(null);
      plcVisualInitializedRef.current = false;
      plcMotionKeyRef.current = null;
      setPlcMotion(null);
      return;
    }

    // À l'ouverture ou après une reconnexion, partir du palier réellement lu
    // avant de lancer une animation déjà en cours.
    if (!plcVisualInitializedRef.current) {
      plcVisualInitializedRef.current = true;
      setPlcVisualLevelValue(PLC_STAGE_LEVELS[plcStage]);
    }

    const previousDirection = plcDirectionRef.current;
    // L31/L32 sont publiés par le serveur TCP du Crouzet sous les noms
    // Remplissage/Vidange. Ils permettent à chaque navigateur de suivre une
    // commande physique ou provenant d'un autre appareil. La pompe constitue
    // en plus une confirmation fiable du remplissage.
    const rawReportedDirection = vidange
      ? "draining"
      : remplissage || pump
        ? "filling"
        : null;
    let reportedDirection = rawReportedDirection;
    if (ignoredPlcDirectionRef.current) {
      if (rawReportedDirection === ignoredPlcDirectionRef.current) {
        reportedDirection = null;
      } else {
        updateIgnoredPlcDirection(null);
      }
    }
    let direction = plcCommandDirection
      ?? reportedDirection
      ?? previousDirection;

    // Dès que le PLC confirme l'ordre optimiste, son retour L31/L32 reprend la
    // main. Avant cette confirmation, l'ancien retour PLC ne doit pas annuler
    // visuellement l'inversion que l'utilisateur vient de demander.
    if (plcCommandDirection && reportedDirection === plcCommandDirection) {
      if (plcCommandDirectionTimeoutRef.current) {
        clearTimeout(plcCommandDirectionTimeoutRef.current);
        plcCommandDirectionTimeoutRef.current = null;
      }
      updatePlcCommandDirection(null);
      direction = reportedDirection;
    }
    const reversingDirection = previousDirection
      && direction
      && previousDirection !== direction;
    if (reversingDirection) {
      updateIgnoredPlcDirection(previousDirection);
      if (
        direction === "draining"
        && plcStage === "low"
        && plcVisualLevelRef.current > LEVEL_LOW + 0.5
      ) {
        plcReturnTargetRef.current = "low";
      } else if (
        direction === "filling"
        && plcStage === "high"
        && plcVisualLevelRef.current < LEVEL_HIGH - 0.5
      ) {
        plcReturnTargetRef.current = "high";
      } else {
        plcReturnTargetRef.current = null;
      }
    }
    if (data?.stop) {
      direction = null;
      updatePlcCommandDirection(null);
      plcReturnTargetRef.current = null;
    }

    const returningToHigh = plcReturnTargetRef.current === "high"
      && direction === "filling"
      && plcVisualLevelRef.current < LEVEL_HIGH - 0.5;
    const returningToLow = plcReturnTargetRef.current === "low"
      && direction === "draining"
      && plcVisualLevelRef.current > LEVEL_LOW + 0.5;
    const fillingComplete = direction === "filling"
      && plcStage === "high"
      && !returningToHigh;
    const drainingComplete = direction === "draining"
      && plcStage === "low"
      && !returningToLow;
    if (fillingComplete || drainingComplete) {
      plcReturnTargetRef.current = null;
      stopPlcVisualMotion();
      plcMotionKeyRef.current = null;
      setPlcVisualLevelValue(direction === "filling" ? LEVEL_HIGH : LEVEL_LOW);
      updatePlcCommandDirection(null);
      direction = null;
    }

    plcDirectionRef.current = direction;

    let targetStage = direction === "filling"
      ? { low: "medium", medium: "high" }[plcStage]
      : direction === "draining"
        ? { high: "medium", medium: "low" }[plcStage]
        : null;
    if (returningToHigh) targetStage = "high";
    if (returningToLow) targetStage = "low";

    if (!targetStage) {
      stopPlcVisualMotion();
      plcMotionKeyRef.current = null;
      setPlcMotion(null);
      if (!data?.stop) setPlcVisualLevelValue(PLC_STAGE_LEVELS[plcStage]);
      return;
    }

    // La maquette évolue par paliers TOR de 5 secondes. Un nouveau segment ne
    // commence qu'après confirmation du palier précédent par le PLC.
    const motionKey = `${direction}:${plcStage}:${targetStage}`;
    if (plcMotionKeyRef.current !== motionKey) {
      const fromLevel = plcVisualLevelRef.current;
      const targetLevel = PLC_STAGE_LEVELS[targetStage];
      const fullSegment = targetStage === "low"
        ? LEVEL_MED - LEVEL_LOW
        : targetStage === "high"
          ? LEVEL_HIGH - LEVEL_MED
          : direction === "filling"
            ? LEVEL_MED - LEVEL_LOW
            : LEVEL_HIGH - LEVEL_MED;
      const durationMs = returningToHigh || returningToLow
        ? PLC_STAGE_DURATION_MS * Math.abs(targetLevel - fromLevel) / fullSegment
        : PLC_STAGE_DURATION_MS;
      startPlcVisualMotion(
        fromLevel,
        targetLevel,
        direction,
        motionKey,
        durationMs,
      );
    }
  }, [
    connected,
    data?.level_percent,
    data?.stop,
    isSimulation,
    plcStage,
    plcCommandDirection,
    plcVisualLevel,
    pump,
    remplissage,
    sharedAnimationDirection,
    sharedLevelAvailable,
    stale,
    startPlcVisualMotion,
    stopPlcVisualMotion,
    setPlcVisualLevelValue,
    updatePlcCommandDirection,
    updateIgnoredPlcDirection,
    vidange,
  ]);

  useEffect(() => () => {
    stopPlcVisualMotion();
    if (plcCommandDirectionTimeoutRef.current) {
      clearTimeout(plcCommandDirectionTimeoutRef.current);
    }
  }, [stopPlcVisualMotion]);

  const levelKnown = isSimulation
    ? Number.isFinite(data?.level_percent)
    : ["low", "medium", "high"].includes(stage);
  const displayLevel = sharedLevelAvailable ? data.level_percent : plcVisualLevel;
  const displayStage = stage ?? "unknown";
  const stageLabel = {
    low: "BAS",
    medium: "MOYEN",
    high: "HAUT",
  }[stage] ?? "INCONNU";
  const isHighStage = stage === "high";
  const isLowStage = stage === "low";
  const displayedMotion = sharedLevelAvailable ? sharedAnimationDirection : plcMotion;
  const plcMotionLabel = displayedMotion === "filling"
    ? "REMPLISSAGE — progression estimée"
    : displayedMotion === "draining"
      ? "VIDANGE — progression estimée"
      : "STABLE";

  const prevHighStageRef = useRef(isHighStage);
  const prevLowStageRef = useRef(isLowStage);
  const prevPumpRef = useRef(pump);

  useEffect(() => {
    if (isHighStage && !prevHighStageRef.current) {
      pushNotif("Réservoir plein", "Niveau haut atteint — arrêt automatique de la pompe", "success");
    }
    prevHighStageRef.current = isHighStage;
  }, [isHighStage, pushNotif]);

  useEffect(() => {
    if (isLowStage && !prevLowStageRef.current) {
      pushNotif("ALERTE : Niveau d'eau critique", "Niveau bas détecté — remplissage nécessaire", "alarm");
    }
    prevLowStageRef.current = isLowStage;
  }, [isLowStage, pushNotif]);

  useEffect(() => {
    if (pump && !prevPumpRef.current) {
      pushNotif("Pompe en fonctionnement", "La pompe est activée", "info");
    }
    prevPumpRef.current = pump;
  }, [pump, pushNotif]);

  const runTime = `${String(Math.floor(secs / 60)).padStart(2,"0")}:${String(secs % 60).padStart(2,"0")}`;

  return (
    <div className="scada-root" style={{ background: C.tealBg, minHeight:"100vh", padding:"0" }}>
      <style>{STYLES}</style>

      <div style={{
        position:"fixed", top:110, left:"50%", transform:"translateX(-50%)", zIndex:999,
        display:"flex", flexDirection:"column", gap:6,
        width:"min(720px, calc(100vw - 32px))",
        pointerEvents:"none",
      }}>
        {notifs.map(n => (
          <Notif key={n.id} msg={n} type={n.type} onClose={() => closeNotif(n.id)}/>
        ))}
      </div>

      {(!connected || stale || liveDataError) && (
        <div className="reservoir-status-banner" role="alert" style={{
          padding:"10px 14px",
          borderRadius:8,
          background:"#fff4e0",
          border:"1px solid #ffd9a0",
          color:"#8a5200",
          fontSize:12,
          fontWeight:700,
        }}>
          {!connected
            ? "BACKEND DÉCONNECTÉ — dernière valeur conservée, commandes désactivées"
            : stale
              ? "DONNÉES OBSOLÈTES — dernière valeur conservée, commandes désactivées"
              : "ERREUR DE DONNÉES"}
          {liveDataError ? ` — ${liveDataError}` : ""}
        </div>
      )}

      <div className="reservoir-layout">
        <div className="reservoir-column reservoir-command-column">
          <div className="glass-card" style={{ padding:"12px" }}>
            <div className="s-title">COMMANDES</div>
            <button
              className={`btn-marche${pump ? " active" : ""}`}
              onClick={cmdMarche}
              disabled={controlsDisabled || isStopping}
              style={{ opacity:controlsDisabled || isStopping ? 0.55 : 1, cursor:controlsDisabled || isStopping ? "not-allowed" : "pointer" }}
            >
              <span style={{fontSize:14}}>▶</span> MARCHE
            </button>
            <button
              className="btn-arret"
              onClick={cmdArret}
              disabled={isStopping || controlsDisabled}
              style={{
                marginTop:8,
                opacity:isStopping || controlsDisabled ? 0.55 : 1,
                cursor:isStopping || controlsDisabled ? "not-allowed" : "pointer",
              }}
            >
              <span style={{fontSize:12}}>■</span>
              {isStopping ? " ARRÊT..." : " ARRÊT"}
            </button>
          </div>

          <div className="glass-card" style={{ padding:"12px" }}>
            <div className="s-title">COMMANDES DE CYCLE</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <SensorButton
                on={fillingCycleActive}
                color="green"
                label="Remplissage"
                onClick={toggleRemplissage}
                disabled={cycleControlsDisabled
                  || isSendingRemplissage
                  || isSendingVidange}
                disabledLabel={!controlsDisabled && !plcStageKnown
                  ? "VERROUILLÉ — niveau inconnu"
                  : "COMMANDE DÉSACTIVÉE"}
              />
              <SensorButton
                on={drainingCycleActive}
                color="red"
                label="Vidange"
                onClick={toggleVidange}
                disabled={cycleControlsDisabled
                  || isSendingVidange
                  || isSendingRemplissage}
                disabledLabel={!controlsDisabled && !plcStageKnown
                  ? "VERROUILLÉ — niveau inconnu"
                  : "COMMANDE DÉSACTIVÉE"}
              />
            </div>
          </div>

          <div className="glass-card" style={{ padding:"12px" }}>
            <div className="s-title">MESURES</div>
            <div style={{ background: C.teal, borderRadius:8, padding:"8px 10px", textAlign:"center" }}>
              <div style={{ fontSize:28, fontWeight:700, color:"#fff", fontFamily:"Rajdhani,monospace", lineHeight:1 }}>
                {data && levelKnown
                  ? isSimulation
                    ? `${Math.round(level)}%`
                    : `NIVEAU ${stageLabel}`
                  : "—"}
              </div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,0.7)", letterSpacing:1.5, marginTop:2 }}>
                {isSimulation ? "NIVEAU SIMULÉ" : "PALIER TOR PLC CONFIRMÉ — NON ANALOGIQUE"}
              </div>
              {!isSimulation && levelKnown && (
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.9)", letterSpacing:.7, marginTop:4 }}>
                  {plcMotionLabel} · ≈ 5 S / PALIER
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="glass-card reservoir-visual" style={{
          padding:"4px 10px",
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          minHeight:320,
          overflow:"hidden",
        }}>
          <ReservoirSVG
            level={levelKnown ? displayLevel : 0}
            stageLabel={stageLabel}
            showPercent={isSimulation}
            outputLow={ledRed}
            outputMedium={ledOrange}
            outputHigh={ledGreen}
            outputPump={pump}
          />
        </div>

        <div className="reservoir-column reservoir-side-column">
          {/* ── Panneau LED (image 1) remplacé par la jauge à aiguille ── */}
          <div className="glass-card" style={{ padding:"12px" }}>
            <div className="s-title">INDICATEUR NIVEAU</div>
            <LevelGauge
              level={levelKnown ? displayLevel : 0}
              stage={displayStage}
              showPercent={isSimulation}
            />
            <div style={{ marginTop:10 }}>
              <Led on={pump} color="teal" label="Pompe" sublabel={pump ? "En marche" : "Arrêtée"}/>
            </div>
          </div>

          <div className="glass-card" style={{ padding:"12px" }}>
            <div className="s-title">ÉTAT SYSTÈME</div>
            {[
              { lbl:"Système", val: pump ? "ACTIF" : "ARRÊT", on: pump, c:"teal" },
              { lbl:"Pompe", val: pump ? "MARCHE" : "STOP", on: pump, c:"teal" },
              { lbl:"Remplissage", val: remplissage ? "ACTIF" : "—", on: remplissage, c:"green" },
              { lbl:"Vidange", val: vidange ? "ACTIF" : "—", on: vidange, c:"red" },
              ...(!isSimulation ? [{ lbl:"Animation", val: plcMotionLabel, on: displayedMotion !== null, c: displayedMotion === "draining" ? "red" : "green" }] : []),
              { lbl:"Palier niveau", val: levelKnown ? stageLabel : "INCONNU", on: levelKnown, c: isLowStage ? "red" : isHighStage ? "green" : "teal" },
              { lbl:"Capteur haut", val: ledGreen ? "ACTIF" : "—", on: ledGreen, c:"green" },
              { lbl:"Capteur bas (cumulatif)", val: ledRed ? "ACTIF" : "—", on: ledRed, c:"red" },
            ].map(r => (
              <div key={r.lbl} style={{
                display:"flex", justifyContent:"space-between", alignItems:"center",
                padding:"5px 8px", background: C.tealBg, borderRadius:6, marginBottom:5,
                border:`1px solid ${C.border2}`,
              }}>
                <span style={{ fontSize:10, fontWeight:600, color:"#555", letterSpacing:.3 }}>{r.lbl}</span>
                <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{
                    width:8, height:8, borderRadius:"50%",
                    background: r.on ? (r.c === "red" ? C.red : r.c === "green" ? C.green : C.teal) : "#ccc",
                  }}/>
                  <span style={{ fontSize:10, fontWeight:700, color: r.on ? (r.c === "red" ? C.red : r.c === "green" ? C.green : C.teal) : "#aaa", letterSpacing:.5 }}>{r.val}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="glass-card" style={{ padding:"12px" }}>
            <div className="s-title">TEMPS MARCHE</div>
            <div style={{ background:"rgba(142,68,173,0.08)", border:`1px solid ${C.purple}44`, borderRadius:8, padding:"8px", textAlign:"center" }}>
              <div style={{ fontSize:22, fontWeight:700, color:C.purple, fontFamily:"Rajdhani,monospace" }}>{runTime}</div>
              <div style={{ fontSize:9, color:C.grayLight, letterSpacing:1.5, marginTop:2 }}>MM:SS</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
